import semver from 'semver';

import type { Misc } from '../types/book';

export type AppVersionInfo = {
  version: string;
  buildNumber: number | null;
  displayVersion: string;
};

export type PendingAppUpdateComparisonScope = 'release' | 'full';

export type PendingAppUpdate = {
  from: AppVersionInfo;
  target: AppVersionInfo;
  startedAt: number;
  lastAttemptAt: number;
  attempts: number;
  comparisonScope: PendingAppUpdateComparisonScope;
  forceUpgrade?: boolean;
  storeUrl?: string | null;
  storeUpdatePromptsEnabled?: boolean;
};

const PENDING_APP_UPDATE_KEY = 'wepub.pendingAppUpdate';
const DISMISSED_APP_UPDATE_PROMPT_KEY = 'wepub.dismissedAppUpdatePrompt';
const MAX_PENDING_APP_UPDATE_AGE_MS = 30 * 60 * 1000;
const DISMISSED_APP_UPDATE_PROMPT_AGE_MS = 24 * 60 * 60 * 1000;
const DEFAULT_PENDING_APP_UPDATE_COMPARISON_SCOPE: PendingAppUpdateComparisonScope = 'release';

type DismissedAppUpdatePrompt = {
  target: AppVersionInfo;
  dismissedAt: number;
};

const normalizeBuildNumber = (value: unknown): number | null => {
  const numericValue = typeof value === 'number' ? value : Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return null;
  }

  return numericValue;
};

const toComparableDisplayVersion = (versionInfo: AppVersionInfo): string =>
  versionInfo.buildNumber === null ? versionInfo.version : `${versionInfo.version}.${versionInfo.buildNumber}`;

const normalizePendingComparisonScope = (
  value: unknown,
): PendingAppUpdateComparisonScope => (value === 'full' ? 'full' : DEFAULT_PENDING_APP_UPDATE_COMPARISON_SCOPE);

const compareWithPendingScope = (
  left: AppVersionInfo,
  right: AppVersionInfo,
  comparisonScope: PendingAppUpdateComparisonScope,
): number => (comparisonScope === 'full' ? compareNativeAppVersionInfo(left, right) : compareAppReleaseVersion(left, right));

export const createAppVersionInfo = (version: string, buildNumber?: unknown): AppVersionInfo => {
  const normalizedBuildNumber = normalizeBuildNumber(buildNumber);
  const displayVersion = normalizedBuildNumber === null ? version : `${version}.${normalizedBuildNumber}`;

  return {
    version,
    buildNumber: normalizedBuildNumber,
    displayVersion,
  };
};

export const formatAppVersionLabel = (versionInfo: AppVersionInfo): string => `v${versionInfo.displayVersion}`;

export const compareAppVersionInfo = (left: AppVersionInfo, right: AppVersionInfo): number => {
  const leftSemver = semver.valid(left.version);
  const rightSemver = semver.valid(right.version);

  if (leftSemver && rightSemver) {
    const semverComparison = semver.compare(leftSemver, rightSemver);
    if (semverComparison !== 0) {
      return semverComparison;
    }
  } else if (left.version !== right.version) {
    return left.version.localeCompare(right.version, undefined, { numeric: true, sensitivity: 'base' });
  }

  if (left.buildNumber !== null || right.buildNumber !== null) {
    const leftBuildNumber = left.buildNumber ?? 0;
    const rightBuildNumber = right.buildNumber ?? 0;
    if (leftBuildNumber !== rightBuildNumber) {
      return leftBuildNumber - rightBuildNumber;
    }
  }

  return toComparableDisplayVersion(left).localeCompare(toComparableDisplayVersion(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const compareNativeAppVersionInfo = (left: AppVersionInfo, right: AppVersionInfo): number => {
  if (left.buildNumber !== null && right.buildNumber !== null && left.buildNumber !== right.buildNumber) {
    return left.buildNumber - right.buildNumber;
  }

  return compareAppVersionInfo(left, right);
};

export const compareAppReleaseVersion = (left: AppVersionInfo, right: AppVersionInfo): number => {
  const leftSemver = semver.valid(left.version);
  const rightSemver = semver.valid(right.version);

  if (leftSemver && rightSemver) {
    return semver.compare(leftSemver, rightSemver);
  }

  return left.version.localeCompare(right.version, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const getAppVersionInfoFromMisc = (misc: Misc | null | undefined): AppVersionInfo | null => {
  if (!misc) {
    return null;
  }

  if (misc.version) {
    return createAppVersionInfo(misc.version, misc.buildNumber);
  }

  if (misc.displayVersion) {
    return createAppVersionInfo(misc.displayVersion);
  }

  return null;
};

export const withAppVersionInMisc = (misc: Misc | null | undefined, versionInfo: AppVersionInfo): Misc => ({
  ...(misc || {}),
  version: versionInfo.version,
  buildNumber: versionInfo.buildNumber,
  displayVersion: versionInfo.displayVersion,
});

const readPendingAppUpdateFromStorage = (): PendingAppUpdate | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.localStorage.getItem(PENDING_APP_UPDATE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as PendingAppUpdate;
    if (!parsed?.target?.version || !parsed?.from?.version) {
      return null;
    }

    return {
      ...parsed,
      from: createAppVersionInfo(parsed.from.version, parsed.from.buildNumber),
      target: createAppVersionInfo(parsed.target.version, parsed.target.buildNumber),
      startedAt: Number(parsed.startedAt || 0),
      lastAttemptAt: Number(parsed.lastAttemptAt || 0),
      attempts: Number(parsed.attempts || 0),
      comparisonScope: normalizePendingComparisonScope(parsed.comparisonScope),
      forceUpgrade: Boolean(parsed.forceUpgrade),
      storeUrl: typeof parsed.storeUrl === 'string' ? parsed.storeUrl : null,
      storeUpdatePromptsEnabled: Boolean(parsed.storeUpdatePromptsEnabled),
    };
  } catch (error) {
    console.error('Failed to parse pending app update state:', error);
    return null;
  }
};

const writePendingAppUpdateToStorage = (pendingUpdate: PendingAppUpdate): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(PENDING_APP_UPDATE_KEY, JSON.stringify(pendingUpdate));
};

const readDismissedAppUpdatePromptFromStorage = (): DismissedAppUpdatePrompt | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.localStorage.getItem(DISMISSED_APP_UPDATE_PROMPT_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as DismissedAppUpdatePrompt;
    const dismissedAt = Number(parsed?.dismissedAt);
    if (!parsed?.target?.version || !Number.isFinite(dismissedAt)) {
      return null;
    }

    return {
      target: createAppVersionInfo(parsed.target.version, parsed.target.buildNumber),
      dismissedAt,
    };
  } catch (error) {
    console.error('Failed to parse dismissed app update prompt state:', error);
    return null;
  }
};

const writeDismissedAppUpdatePromptToStorage = (dismissedPrompt: DismissedAppUpdatePrompt): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(DISMISSED_APP_UPDATE_PROMPT_KEY, JSON.stringify(dismissedPrompt));
};

export const clearPendingAppUpdate = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(PENDING_APP_UPDATE_KEY);
};

export const clearDismissedAppUpdatePrompt = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(DISMISSED_APP_UPDATE_PROMPT_KEY);
};

export const dismissAppUpdatePrompt = (targetVersion: AppVersionInfo, now = Date.now()): void => {
  writeDismissedAppUpdatePromptToStorage({
    target: targetVersion,
    dismissedAt: now,
  });
};

export const isAppUpdatePromptDismissed = (targetVersion: AppVersionInfo, now = Date.now()): boolean => {
  const dismissedPrompt = readDismissedAppUpdatePromptFromStorage();
  if (!dismissedPrompt) {
    return false;
  }

  if (now - dismissedPrompt.dismissedAt > DISMISSED_APP_UPDATE_PROMPT_AGE_MS) {
    clearDismissedAppUpdatePrompt();
    return false;
  }

  return compareAppVersionInfo(dismissedPrompt.target, targetVersion) === 0;
};

export const getPendingAppUpdate = (now = Date.now()): PendingAppUpdate | null => {
  const pendingUpdate = readPendingAppUpdateFromStorage();
  if (!pendingUpdate) {
    return null;
  }

  if (now - pendingUpdate.startedAt > MAX_PENDING_APP_UPDATE_AGE_MS) {
    clearPendingAppUpdate();
    return null;
  }

  return pendingUpdate;
};

export const beginPendingAppUpdate = (
  currentVersion: AppVersionInfo,
  targetVersion: AppVersionInfo,
  now = Date.now(),
  options?: {
    comparisonScope?: PendingAppUpdateComparisonScope;
    forceUpgrade?: boolean;
    storeUrl?: string | null;
    storeUpdatePromptsEnabled?: boolean;
  },
): PendingAppUpdate => {
  const comparisonScope = options?.comparisonScope ?? DEFAULT_PENDING_APP_UPDATE_COMPARISON_SCOPE;
  const existingPendingUpdate = getPendingAppUpdate(now);
  const isSameTarget =
    !!existingPendingUpdate &&
    existingPendingUpdate.comparisonScope === comparisonScope &&
    compareAppReleaseVersion(existingPendingUpdate.target, targetVersion) === 0;

  const pendingUpdate: PendingAppUpdate = {
    from: isSameTarget ? existingPendingUpdate.from : currentVersion,
    target: targetVersion,
    startedAt: isSameTarget ? existingPendingUpdate.startedAt : now,
    lastAttemptAt: now,
    attempts: isSameTarget ? existingPendingUpdate.attempts + 1 : 1,
    comparisonScope,
    forceUpgrade: options?.forceUpgrade ?? existingPendingUpdate?.forceUpgrade ?? false,
    storeUrl: options?.storeUrl ?? existingPendingUpdate?.storeUrl ?? null,
    storeUpdatePromptsEnabled:
      options?.storeUpdatePromptsEnabled ?? existingPendingUpdate?.storeUpdatePromptsEnabled ?? false,
  };

  writePendingAppUpdateToStorage(pendingUpdate);
  return pendingUpdate;
};

export const resolvePendingAppUpdate = (
  currentVersion: AppVersionInfo,
  now = Date.now(),
): { status: 'none' | 'waiting' | 'updated'; pendingUpdate: PendingAppUpdate | null } => {
  const pendingUpdate = getPendingAppUpdate(now);
  if (!pendingUpdate) {
    return { status: 'none', pendingUpdate: null };
  }

  if (compareWithPendingScope(currentVersion, pendingUpdate.target, pendingUpdate.comparisonScope) >= 0) {
    clearPendingAppUpdate();
    clearDismissedAppUpdatePrompt();
    return { status: 'updated', pendingUpdate };
  }

  return { status: 'waiting', pendingUpdate };
};

export const MAX_APP_UPDATE_AUTO_RELOAD_ATTEMPTS = 3;
